import { EffectOptions } from '../../app';
import { APP_INITIALIZED } from '../../app/state/app.types';
import { selectIdleSession } from '../../configuration';
import {
    fetchGetExtendSessionRequest,
    SESSION_CLEAR,
    SESSION_EXTENDED,
    sessionIdleTimedOut
} from '../session.actions';
import {
    minutesToMilliseconds,
    secondsToMinutes
} from '@utils';

const IDLE_INVALIDATION_EVENTS = [
    'visibilitychange',
    'keypress',
    'keydown',
    'click',
    'contextmenu',
    'dblclick',
    'mousemove',
    'scroll',
    'touchmove',
    'touchstart'
];
const INIT_INTERVAL = 1;
const THROTTLE = 2;

let hasInitialised = false;
let currentInterval = INIT_INTERVAL;
let sessionTimer: null | number = null;
let lastInteractionTime = 0;

export function idleSessionEffects ({
    getState,
    action,
    dispatch
}: EffectOptions) {
    const state = getState();
    const {
        sessionOverrideTimeInMinutes,
        sessionExpiryTimeInMinutes,
        sessionBufferTimeInSeconds,
        countdownTimeoutInMinutes
    } = selectIdleSession(state);
    const isInitialiseAction = action.type === APP_INITIALIZED;
    const isSessionExtendedAction = action.type === SESSION_EXTENDED;
    const isSessionClearAction = action.type === SESSION_CLEAR;
    // We create a slight buffer to cater for system time
    const intervalTimeWithBuffer = sessionExpiryTimeInMinutes - secondsToMinutes(sessionBufferTimeInSeconds);
    const finalIntervalBuffer = sessionExpiryTimeInMinutes - countdownTimeoutInMinutes - secondsToMinutes(sessionBufferTimeInSeconds);
    // The amount of intervals required to meet the availability time
    const totalIntervals = Math.ceil(sessionOverrideTimeInMinutes / sessionExpiryTimeInMinutes);
    const throttleTime = minutesToMilliseconds(THROTTLE);

    if (isInitialiseAction || isSessionExtendedAction) {
        if (!hasInitialised) init();

        return;
    }

    if (isSessionClearAction) cleanup();

    function init () {
        hasInitialised = true;

        console.log({
            type: 'INIT',
            message: 'timer has initialised and process has begun',
            sessionOverrideTimeInMinutes,
            sessionExpiryTimeInMinutes,
            sessionBufferTimeInSeconds,
            countdownTimeoutInMinutes,
            finalIntervalBuffer,
            intervalTimeWithBuffer,
            totalIntervals,
            timeNow: new Date().toLocaleString('en-AU')
        });

        addUserInteractionEvents();
        initSessionExtend();
    }

    function cleanup () {
        hasInitialised = false;

        removeUserInteractionEvents();
        clearSessionTimer();
    }

    function addUserInteractionEvents () {
        removeUserInteractionEvents();

        IDLE_INVALIDATION_EVENTS.forEach((event) => window.document.addEventListener(event, onUserInteraction));
    }
    function removeUserInteractionEvents () {
        IDLE_INVALIDATION_EVENTS.forEach((event) => window.document.removeEventListener(event, onUserInteraction));
    }

    function onUserInteraction () {
        throttle(initSessionExtend);
    }

    function initSessionExtend () {
        clearSessionTimer();
        sessionTimer = window.setInterval(() => {
            const isFinalInterval = currentInterval >= totalIntervals - 1;

            dispatch(fetchGetExtendSessionRequest());

            if (isFinalInterval) {
                console.log({
                    type: 'EXTEND INTERVAL FINAL',
                    message: 'interval has increase',
                    currentInterval,
                    totalIntervals,
                    time: new Date().toLocaleString('en-AU')
                });
                clearSessionTimer();
                setTimeout(() => {
                    console.log({
                        type: 'CLEANUP',
                        message: 'session will time out in 5 mins',
                        currentInterval,
                        totalIntervals,
                        timeEnd: new Date().toLocaleString('en-AU')
                    });
                    cleanup();
                    dispatch(sessionIdleTimedOut());
                }, minutesToMilliseconds(finalIntervalBuffer));
            } else {
                currentInterval++;
                console.log({
                    type: 'EXTEND INTERVAL',
                    message: 'interval has increase',
                    currentInterval,
                    totalIntervals,
                    totalTime: sessionExpiryTimeInMinutes * currentInterval,
                    time: new Date().toLocaleString('en-AU')
                });
            }
        }, minutesToMilliseconds(intervalTimeWithBuffer));
    }

    function clearSessionTimer () {
        if (sessionTimer) clearInterval(sessionTimer);

        sessionTimer = null;
    }

    function throttle (callback: any) {
        const now = Date.now();

        if (now - lastInteractionTime >= throttleTime) {
            console.log({
                type: 'RESET',
                message: 'timer has reset as user has interacted with page',
                sessionOverrideTimeInMinutes,
                sessionExpiryTimeInMinutes,
                sessionBufferTimeInSeconds,
                intervalTimeWithBuffer,
                totalIntervals,
                totalTime: sessionExpiryTimeInMinutes * currentInterval,
                time: new Date().toLocaleString('en-AU')
            });
            dispatch(fetchGetExtendSessionRequest());
            currentInterval = INIT_INTERVAL;
            lastInteractionTime = now;
            callback();
        }
    }
}
